// pages/PresenterPage.tsx
//
// PUBLIC presenter brand page at /p/:uid — the shareable URL a host drops in
// their stream description. Shows who they are + the announced NEXT LIVE QUIZ
// (the funnel's return hook). Single-uid one-time read (rules allow any
// signed-in user); nothing here subscribes, so zero standing fan-out.
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { PresenterProfile } from '../types/presenter.types';
import { WindowBoard } from '../types/quiz.types';
import { getPresenterProfile, isFollowing, setFollowing } from '../services/presenter';
import { track } from '../services/analytics';
import { getWindowBoards, currentPeriods } from '../services/live-session';
import { useAuth } from '../context/Auth/auth-context';
import { rankChip } from '../constants/answer-tiles';

// Keep an announced show on the page for a few hours after start — "we're live
// right now" is the best possible state for this page to be in.
const SHOW_LINGER_MS = 4 * 60 * 60 * 1000;

const linkButton = (label: string, url?: string) =>
  url ? (
    <a
      key={label}
      href={url}
      target="_blank"
      rel="noreferrer"
      className="btn text-sm text-ink bg-ink/10 hover:bg-ink/15"
    >
      {label}
    </a>
  ) : null;

// A windowed champions list, labelled by window + (non-main) show tag.
const BoardCard = ({ title, tag, board }: { title: string; tag?: string; board: WindowBoard }) => (
  <div className="card p-6 mt-4">
    <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-3">
      {title}
      {tag && <span className="ml-2 normal-case font-medium text-slate-300">· {tag}</span>}
      {board.totalPlayers > 0 && (
        <span className="ml-2 normal-case font-medium text-slate-300">· {board.totalPlayers} player{board.totalPlayers === 1 ? '' : 's'}</span>
      )}
    </p>
    <ol className="space-y-2">
      {board.ranks.map((e, i) => (
        <li key={e.uid} className={`flex items-center gap-3 p-2.5 rounded-xl ${i < 3 ? 'bg-accent/15' : 'bg-slate-50'}`}>
          <span className={rankChip(i)}>{i + 1}</span>
          <span className="flex-1 font-medium truncate">{e.avatar ? `${e.avatar} ` : ''}{e.name}</span>
          <span className="font-display font-bold text-ink tabular-nums">{e.score.toLocaleString()}</span>
        </li>
      ))}
    </ol>
  </div>
);

export const PresenterPage = () => {
  const { uid } = useParams<{ uid: string }>();
  const { user } = useAuth();
  const [profile, setProfile] = useState<PresenterProfile | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [follows, setFollows] = useState<boolean | null>(null);
  const [boards, setBoards] = useState<Record<string, Record<string, WindowBoard>>>({});

  useEffect(() => {
    if (!uid) return;
    getPresenterProfile(uid)
      .then(setProfile)
      .finally(() => setLoaded(true));
    // Fresh-winners surface: this presenter's current day/week boards. One-time
    // get of tiny top-N snapshots — no subscription.
    getWindowBoards(uid).then(setBoards).catch(() => {});
  }, [uid]);

  // Follow state — a single-child one-time get; only for signed-in visitors
  // who aren't looking at their own page.
  useEffect(() => {
    if (!uid || !user || user === uid) return;
    isFollowing(uid, user).then(setFollows).catch(() => {});
  }, [uid, user]);

  const toggleFollow = async () => {
    if (!uid || !user || follows === null) return;
    const next = !follows;
    setFollows(next); // optimistic
    try {
      await setFollowing(uid, user, next);
      if (next) track('follow_presenter', { where: 'presenter_page' });
    } catch { setFollows(!next); }
  };

  if (!loaded) {
    return <div className="max-w-xl mx-auto p-6 text-slate-400">Loading…</div>;
  }

  if (!profile) {
    return (
      <div className="max-w-xl mx-auto p-10 text-center">
        <h2 className="text-2xl font-bold font-display tracking-tight mb-2">No such presenter</h2>
        <p className="text-slate-500">This page doesn't exist (yet).</p>
        <Link to="/" className="btn-primary inline-block mt-5">Join a game</Link>
      </div>
    );
  }

  const show = profile.nextShow;
  const showIsCurrent = Boolean(show && show.scheduledAt > Date.now() - SHOW_LINGER_MS);
  const isLiveNow = Boolean(show && showIsCurrent && show.scheduledAt <= Date.now());

  // Pick out this day's/week's boards across every show tag (stale windows in
  // the blob are simply ignored).
  const { day, week } = currentPeriods();
  const dayBoards: Array<{ tag: string; board: WindowBoard }> = [];
  const weekBoards: Array<{ tag: string; board: WindowBoard }> = [];
  for (const [tag, byPeriod] of Object.entries(boards)) {
    if (byPeriod?.[day]?.ranks?.length) dayBoards.push({ tag, board: byPeriod[day] });
    if (byPeriod?.[week]?.ranks?.length) weekBoards.push({ tag, board: byPeriod[week] });
  }

  return (
    <div className="max-w-xl mx-auto p-4 sm:p-6">
      {/* Identity */}
      <div className="card p-6 text-center">
        {profile.photoURL ? (
          <img
            src={profile.photoURL}
            alt=""
            className="w-20 h-20 rounded-full ring-2 ring-slate-200 mx-auto mb-3 object-cover"
          />
        ) : (
          <span className="grid place-items-center w-20 h-20 rounded-full bg-slate-200 text-2xl font-bold mx-auto mb-3">
            {(profile.name || '?').charAt(0).toUpperCase()}
          </span>
        )}
        <h1 className="text-2xl font-extrabold font-display tracking-tight">{profile.name}</h1>
        {profile.tagline && <p className="text-slate-500 mt-1">{profile.tagline}</p>}
        {profile.bio && (
          <p className="text-sm text-slate-600 mt-4 whitespace-pre-line text-left">{profile.bio}</p>
        )}
        <div className="flex flex-wrap justify-center gap-2 mt-4">
          {linkButton('YouTube', profile.links?.youtube)}
          {linkButton('Instagram', profile.links?.instagram)}
          {linkButton('X', profile.links?.twitter)}
          {linkButton('Website', profile.links?.website)}
        </div>
        {follows !== null && (
          <button
            onClick={toggleFollow}
            className={`mt-4 ${follows ? 'btn-ghost !text-ink !bg-ink/10 hover:!bg-ink/15' : 'btn-primary'}`}
          >
            {follows ? '✓ Following' : '🔔 Follow — never miss a show'}
          </button>
        )}
      </div>

      {/* Next show — the headline */}
      <div className="card p-6 mt-4">
        <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-2">
          {isLiveNow ? 'Live now' : 'Next live quiz'}
        </p>
        {show && showIsCurrent ? (
          <>
            <h2 className="text-xl font-extrabold font-display tracking-tight">{show.title}</h2>
            <p className="text-slate-600 mt-1">
              {new Date(show.scheduledAt).toLocaleString(undefined, {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
                hour: 'numeric',
                minute: '2-digit',
              })}
            </p>
            <div className="flex flex-col sm:flex-row gap-2 mt-4">
              {show.youtubeUrl && (
                <a href={show.youtubeUrl} target="_blank" rel="noreferrer" className="btn-primary flex-1 text-center">
                  {isLiveNow ? 'Watch the stream' : 'Set a reminder on YouTube'}
                </a>
              )}
              <Link to="/" className={`${show.youtubeUrl ? 'btn-ghost !text-ink !bg-ink/10 hover:!bg-ink/15' : 'btn-primary'} flex-1 text-center`}>
                Join with the game PIN
              </Link>
            </div>
            <p className="text-xs text-slate-400 mt-3">
              The 6-digit PIN is announced on the stream when the game opens.
            </p>
          </>
        ) : (
          <p className="text-slate-500">
            Nothing announced yet — check back soon, or follow the links above so you don't miss the next one.
          </p>
        )}
      </div>

      {/* Fresh winners — today's and this week's champions per show. Every
          window starts a new race, so new names keep landing here. */}
      {dayBoards.map(({ tag, board }) => (
        <BoardCard key={`d-${tag}`} title="Today's champions" tag={tag !== 'main' ? tag : undefined} board={board} />
      ))}
      {weekBoards.map(({ tag, board }) => (
        <BoardCard key={`w-${tag}`} title="This week's champions" tag={tag !== 'main' ? tag : undefined} board={board} />
      ))}
    </div>
  );
};
